import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const download = (content, filename, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  return { filename, bytes: blob.size, type };
};

const escapeXml = value => String(value ?? "")
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&apos;");

const toXml = (name, rows) => `<${name}>${rows.map(row => `<row>${Object.entries(row).map(([key, value]) => `<${key}>${escapeXml(value)}</${key}>`).join("")}</row>`).join("")}</${name}>`;

/**
 * The rows the reports panel actually holds, serialised for download.
 *
 * `sellers` is the /admin/reports/sales?group_by=category shape — `{bucket, revenue,
 * qty}` (app/api/reports.py:85) — which is what ReportsPanelV2 renders as "Top
 * categories · Starters · 41 sold". This function used to read `row.name` and
 * `row.orders`, keys those rows have never had, so every exported file carried a rank
 * and a revenue column beside a blank category and a blank quantity: the filed report
 * contradicted the dashboard it was exported from, and only the accountant found out.
 * The `?? row.name` / `?? row.orders` fallbacks keep an item-shaped caller readable
 * rather than silently blank, which is the failure mode being closed here.
 */
export const reportBundle = ({ period, stats, sales, payments, sellers }) => ({
  metadata: [{ report: "RestoYaar Sales Report", period, generatedAt: new Date().toISOString() }],
  summary: stats.map(([metric, value, change]) => ({ metric, value, change })),
  hourlySales: sales.map(row => ({ time: row.time, salesINR: row.sales })),
  paymentModes: payments.map(row => ({ mode: row.name, amountINR: row.value })),
  topCategories: (sellers || []).map((row, index) => ({
    rank: index + 1,
    category: row.bucket ?? row.name ?? "",
    qty: row.qty ?? row.orders ?? 0,
    revenueINR: row.revenue,
    ...(row.amountWithTax != null ? { totalWithTaxINR: row.amountWithTax } : {}),
  })),
});

export const exportReport = (format, bundle, filename = "restoyaar-report") => {
  const sections = Object.entries(bundle);
  if (format === "json") return download(JSON.stringify(bundle, null, 2), `${filename}.json`, "application/json");
  if (format === "xml") {
    const xml = `<?xml version="1.0" encoding="UTF-8"?><restoYaarReport>${sections.map(([name, rows]) => toXml(name, rows)).join("")}</restoYaarReport>`;
    return download(xml, `${filename}.xml`, "application/xml");
  }
  if (format === "csv") {
    const rows = sections.flatMap(([section, records]) => records.map(record => ({ section, ...record })));
    return download(XLSX.utils.sheet_to_csv(XLSX.utils.json_to_sheet(rows)), `${filename}.csv`, "text/csv;charset=utf-8");
  }
  if (format === "xlsx") {
    const workbook = XLSX.utils.book_new();
    sections.forEach(([name, rows]) => XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), name.slice(0, 31)));
    const workbookBytes = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    return download(workbookBytes, `${filename}.xlsx`, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
  }
  const pdf = new jsPDF();
  pdf.setFontSize(18);
  pdf.text("RestoYaar - Sales Report", 14, 18);
  pdf.setFontSize(10);
  pdf.text(`Period: ${bundle.metadata[0].period}  |  Generated: ${new Date().toLocaleString("en-IN")}`, 14, 26);
  let y = 34;
  sections.filter(([name]) => name !== "metadata").forEach(([name, rows]) => {
    pdf.setFontSize(12);
    pdf.text(name.replace(/([A-Z])/g, " $1").toUpperCase(), 14, y);
    autoTable(pdf, { startY: y + 3, head: [Object.keys(rows[0] || {})], body: rows.map(row => Object.values(row)), styles: { fontSize: 8 }, theme: "grid" });
    y = pdf.lastAutoTable.finalY + 12;
    if (y > 250) { pdf.addPage(); y = 18; }
  });
  return download(pdf.output("arraybuffer"), `${filename}.pdf`, "application/pdf");
};

